/**
 * Partner Center — public directory and individual profile pages.
 *
 * Routes:
 *   /partners           → PartnersIndex (list grouped by strategic group)
 *   /partners/:slug     → PartnerProfile (full page)
 *
 * Reuses shared `SUPPLIERS` + `STRATEGIC_GROUPS` from /data/partners.js.
 */
import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, FileText, Globe, Mail, Phone, Users,
  ShieldCheck, Sparkles, ChevronRight, Building2, MapPin,
} from "lucide-react";
import { STRATEGIC_GROUPS, SUPPLIERS, partnerBySlug, groupById } from "@/data/partners";
import { useT, LanguageSwitcher } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.62)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  blue:    "#3B82F6",
  amber:   "#F59E0B",
};

// -------------------------------------------------------- INDEX (/partners)

const PartnerCard = ({ p, groupAccent }) => (
  <Link
    to={`/partners/${p.id}`}
    data-testid={`partner-card-${p.id}`}
    className="group rounded-lg p-5 transition hover:border-white/25 block"
    style={{ background: P.glass, border: `1px solid ${P.border}`, backdropFilter: "blur(14px)" }}
  >
    <div className="flex items-center gap-3 mb-3">
      {p.logo ? (
        <div className="w-12 h-12 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.94)" }}>
          <img src={p.logo} alt={p.name} className="max-h-8 max-w-9 object-contain" />
        </div>
      ) : (
        <div className="w-12 h-12 rounded flex items-center justify-center shrink-0 font-bold text-[18px]" style={{ background: `${groupAccent}22`, color: groupAccent }}>{p.name[0]}</div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold" style={{ color: "#fff" }}>{p.name}</div>
        <div className="text-[10px] uppercase tracking-[0.22em] mt-0.5" style={{ color: groupAccent }}>{p.category}</div>
      </div>
      <ArrowUpRight size={14} style={{ color: P.silver }} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </div>
    {p.status && p.status !== "Active Partner" && (
      <div className="mb-2 inline-block text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded" style={{ background: `${P.amber}18`, color: P.amber, border: `1px solid ${P.amber}44` }}>
        {p.status}
      </div>
    )}
    <p className="text-[12px] leading-snug line-clamp-3" style={{ color: P.dim }}>{p.description}</p>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {p.tags.slice(0, 4).map((t) => (
        <span key={t} className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.04)", color: P.silver, border: `1px solid ${P.border}` }}>{t}</span>
      ))}
    </div>
  </Link>
);

export const PartnersIndex = () => {
  const { t } = useT();
  const tp = t.partners_page;
  const grouped = STRATEGIC_GROUPS.map((g) => ({ ...g, suppliers: SUPPLIERS.filter((s) => s.group === g.id) })).filter((g) => g.suppliers.length > 0);

  return (
    <div className="min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="page-partners">
      <LanguageSwitcher />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <Link to="/" data-testid="partners-back-home" className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em]" style={{ color: P.silver }}>
          <ArrowLeft size={12} /> {tp.backHome}
        </Link>
        <div className="mt-6 mb-14">
          <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.blue }}>{tp.eyebrow}</div>
          <h1 className="mt-3 font-semibold tracking-tight text-[36px] lg:text-[52px] leading-[1.02]" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {tp.title}
          </h1>
          <p className="mt-4 max-w-2xl text-[14px] leading-relaxed" style={{ color: P.dim }}>
            {tp.subtitle}
          </p>
        </div>

        <div className="space-y-12">
          {grouped.map((g) => (
            <section key={g.id} data-testid={`partners-group-${g.id}`}>
              <div className="flex items-baseline justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: g.accent }}>
                  {g.label} <span className="opacity-60">· {g.suppliers.length}</span>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.suppliers.map((p) => <PartnerCard key={p.id} p={p} groupAccent={g.accent} />)}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------- PROFILE (/partners/:slug)

const StatSlab = ({ label, value, accent }) => (
  <div className="rounded-md p-4" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
    <div className="text-[9.5px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>{label}</div>
    <div className="mt-1.5 text-[15px] font-semibold" style={{ color: accent || "#fff" }}>{value}</div>
  </div>
);

const RelatedPartners = ({ current }) => {
  const { t } = useT();
  const others = SUPPLIERS.filter((s) => s.group === current.group && s.id !== current.id).slice(0, 3);
  if (others.length === 0) return null;
  const group = groupById(current.group);
  return (
    <div className="mt-14">
      <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-4" style={{ color: group?.accent || P.blue }}>
        {t.partners_page.relatedIn.replace("{group}", group?.label || "")}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {others.map((o) => (
          <Link
            key={o.id}
            to={`/partners/${o.id}`}
            data-testid={`partner-related-${o.id}`}
            className="group rounded-lg p-4 flex items-center gap-3 transition hover:border-white/25"
            style={{ background: P.glass, border: `1px solid ${P.border}` }}
          >
            <div className="w-10 h-10 rounded flex items-center justify-center shrink-0 font-bold text-[15px]" style={{ background: `${group?.accent || P.blue}22`, color: group?.accent || P.blue }}>
              {o.name[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate" style={{ color: "#fff" }}>{o.name}</div>
              <div className="text-[10.5px] truncate" style={{ color: P.dim }}>{o.category}</div>
            </div>
            <ChevronRight size={13} style={{ color: P.silver }} className="transition group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </div>
  );
};

export const PartnerProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const partner = partnerBySlug(slug);
  const group = partner ? groupById(partner.group) : null;
  const { t } = useT();
  const tp = t.partners_page;

  React.useEffect(() => {
    if (partner) {
      document.title = `${partner.name} · NeuNexus Partner Center`;
    }
  }, [partner]);

  if (!partner) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: P.bg, color: P.ink }} data-testid="partner-not-found">
        <LanguageSwitcher />
        <div className="text-center">
          <div className="text-[36px] font-semibold" style={{ color: "#fff" }}>404</div>
          <div className="mt-2 text-[13px]" style={{ color: P.dim }}>{tp.notFound}</div>
          <button onClick={() => navigate("/partners")} className="mt-6 px-4 py-2 rounded-md text-[12px] font-semibold" style={{ background: P.blue, color: "#fff" }}>
            {tp.notFoundCta}
          </button>
        </div>
      </div>
    );
  }

  const accent = group?.accent || P.blue;
  const isProposed = (partner.status || "").toLowerCase().includes("proposed");
  const website = partner.contact?.website;
  const email = partner.contact?.email;
  const phone = partner.contact?.phone;

  return (
    <div className="min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid={`page-partner-${partner.id}`}>
      <LanguageSwitcher />
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent}18 0%, ${P.bg} 60%)`, borderBottom: `1px solid ${P.border}` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-16 lg:pb-20">
          <Link to="/partners" data-testid="partner-back" className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em]" style={{ color: P.silver }}>
            <ArrowLeft size={12} /> {tp.backCenter}
          </Link>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8">
            <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: accent }} data-testid="partner-group-label">
              {group?.label}
            </div>
            <div className="mt-3 flex items-center gap-5 flex-wrap">
              {partner.logo ? (
                <div className="w-20 h-20 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.94)" }}>
                  <img src={partner.logo} alt={partner.name} className="max-h-14 max-w-14 object-contain" />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-md flex items-center justify-center shrink-0 font-bold text-[36px]" style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }} data-testid="partner-logo-initial">
                  {partner.name[0]}
                </div>
              )}
              <div>
                <h1 className="font-semibold tracking-tight text-[36px] lg:text-[48px] leading-[1.02]" style={{ color: "#fff", letterSpacing: "-0.02em" }} data-testid="partner-name">
                  {partner.name}
                </h1>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {partner.status && (
                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold px-2.5 py-1 rounded" style={{ background: isProposed ? `${P.amber}18` : `${P.green}18`, color: isProposed ? P.amber : P.green, border: `1px solid ${isProposed ? P.amber : P.green}44` }} data-testid="partner-status">
                      {partner.status}
                    </span>
                  )}
                  {partner.country && (
                    <span className="text-[11px] inline-flex items-center gap-1" style={{ color: P.dim }}>
                      <MapPin size={11} /> {partner.country}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="mt-8 max-w-3xl text-[14.5px] leading-relaxed" style={{ color: P.ink }} data-testid="partner-description">
              {partner.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10">
          {/* Left column */}
          <div>
            {/* Contribution / tags */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3" style={{ color: accent }}>{tp.contribution}</div>
              <div className="flex flex-wrap gap-2">
                {partner.tags.map((t) => (
                  <span key={t} data-testid={`partner-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-[11px] px-3 py-1.5 rounded-md" style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Category */}
            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3" style={{ color: accent }}>{tp.category}</div>
              <div className="text-[15px] font-semibold" style={{ color: "#fff" }}>{partner.category}</div>
            </div>

            {/* Documents */}
            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3" style={{ color: accent }}>
                {tp.docs} <span className="opacity-60">· {partner.documents.length}</span>
              </div>
              {partner.documents.length === 0 ? (
                <div className="rounded-lg p-5 text-[12px]" style={{ background: P.glass, border: `1px dashed ${P.border}`, color: P.dim }} data-testid="partner-docs-empty">
                  No technical documents linked yet. Documents uploaded via the BS Assistente will appear here automatically.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {partner.documents.map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noopener noreferrer"
                      data-testid={`partner-doc-${i}`}
                      className="flex items-center gap-3 p-3 rounded-md transition hover:border-white/25"
                      style={{ background: P.glass, border: `1px solid ${P.border}` }}>
                      <FileText size={13} style={{ color: accent }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] truncate" style={{ color: "#fff" }}>{d.name}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: P.dim }}>{d.type} · {d.size}</div>
                      </div>
                      <ArrowUpRight size={11} style={{ color: P.silver }} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA: BS Assistente filtered by supplier */}
            <div className="mt-10 rounded-lg p-6" style={{ background: `linear-gradient(135deg, ${P.blue}18 0%, ${P.bg2} 60%)`, border: `1px solid ${P.blue}55` }}>
              <div className="flex items-start gap-4">
                <Sparkles size={20} style={{ color: P.blue }} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.blue }}>{tp.assistantEyebrow}</div>
                  <div className="mt-1 text-[15px] font-semibold" style={{ color: "#fff" }}>{tp.assistantTitle.replace("{name}", partner.name)}</div>
                  <div className="mt-2 text-[12.5px]" style={{ color: P.dim }}>
                    {tp.assistantDesc}
                  </div>
                </div>
                <Link
                  to="/ops"
                  data-testid="partner-open-assistant"
                  className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md whitespace-nowrap"
                  style={{ background: P.blue, color: "#fff" }}
                >
                  {tp.assistantCta} <ArrowUpRight size={12} />
                </Link>
              </div>
            </div>

            <RelatedPartners current={partner} />
          </div>

          {/* Right column — contacts + stats */}
          <aside className="space-y-4">
            <div className="rounded-lg p-5" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3" style={{ color: accent }}>{tp.contact}</div>
              {partner.contact?.name && (
                <div className="flex items-center gap-2 mb-3">
                  <Users size={12} style={{ color: P.silver }} />
                  <span className="text-[12.5px]" style={{ color: P.ink }} data-testid="partner-contact-name">{partner.contact.name}</span>
                </div>
              )}
              {email && (
                <a href={`mailto:${email}`} data-testid="partner-contact-email" className="flex items-center gap-2 mb-2 hover:opacity-80">
                  <Mail size={12} style={{ color: accent }} />
                  <span className="text-[12px]" style={{ color: accent }}>{email}</span>
                </a>
              )}
              {phone && (
                <div className="flex items-center gap-2 mb-2" data-testid="partner-contact-phone">
                  <Phone size={12} style={{ color: P.silver }} />
                  <span className="text-[12px]" style={{ color: P.ink }}>{phone}</span>
                </div>
              )}
              {website && (
                <a href={website} target="_blank" rel="noopener noreferrer" data-testid="partner-contact-website" className="flex items-center gap-2 hover:opacity-80">
                  <Globe size={12} style={{ color: accent }} />
                  <span className="text-[12px] truncate" style={{ color: accent }}>{website.replace(/^https?:\/\//, "")}</span>
                </a>
              )}
              {!email && !phone && !website && (
                <div className="text-[11.5px]" style={{ color: P.dim }}>{tp.contactEmpty}</div>
              )}
            </div>

            <StatSlab label={tp.country}          value={partner.country || "—"}                     accent="#fff" />
            <StatSlab label={tp.category}         value={partner.category}                            accent={accent} />
            <StatSlab label={tp.documents}        value={`${partner.documents.length} ${tp.docsLinked}`} accent="#fff" />
            <StatSlab label={tp.strategicGroup}   value={group?.label || "—"}                          accent={accent} />

            <div className="rounded-lg p-5" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={13} style={{ color: accent }} />
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: accent }}>{tp.engagement}</div>
              </div>
              <div className="text-[12.5px]" style={{ color: P.ink }}>
                {isProposed ? tp.engagementProposed : tp.engagementActive}
              </div>
            </div>

            <div className="rounded-lg p-5" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
              <div className="flex items-center gap-2 mb-2">
                <Building2 size={13} style={{ color: P.silver }} />
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>{tp.ecosystemRole}</div>
              </div>
              <div className="text-[12.5px]" style={{ color: P.ink }}>
                {tp.ecosystemRoleDesc.replace("{group}", group?.label || "").replace("{name}", partner.name)}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <footer className="border-t py-10 text-center" style={{ borderColor: P.border }}>
        <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>NeuNexus · Industrial Innovation Platform · Germany</div>
        <div className="mt-2 text-[11px]" style={{ color: P.dim }}>© 2026 · Integrated & Operated by BSGOTECH · Uetze · Lower Saxony</div>
      </footer>
    </div>
  );
};
